import { type FormEvent, useState } from 'react';
import { api, errorMessage } from '../api';
import { formatSignedEuros } from '../money';
import { today } from '../time';

/**
 * Le solde du compte patient : Σ paiements − Σ factures émises. Négatif, il est rouge —
 * c'est de l'argent qu'on lui doit. La même pastille sur la carte du Rapport et sur
 * la fiche patient : un seul compte, une seule lecture.
 */
export function SoldeChip({ soldeEur }: { soldeEur: number }) {
  const tone = soldeEur < 0 ? ' solde--due' : soldeEur > 0 ? ' solde--credit' : '';
  return <span className={`solde${tone}`}>Solde : {formatSignedEuros(soldeEur)}</span>;
}

interface PaymentFormProps {
  patientId: number;
  /** Le solde, le bandeau, les débiteurs, le trimestre : tout bouge d'un paiement. */
  onAdded: () => void;
}

/** Deux gestes : un montant, une date — celle du jour par défaut. */
export function PaymentForm({ patientId, onAdded }: PaymentFormProps) {
  const [amount, setAmount] = useState('');
  const [paidOn, setPaidOn] = useState(today());
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const amountEur = Number(amount.replace(',', '.'));
  const valid = amount.trim() !== '' && Number.isFinite(amountEur) && amountEur > 0;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!valid) return;
    setPending(true);
    setError(null);
    try {
      await api.payments.create(patientId, { amountEur, paidOn });
      setAmount('');
      setPaidOn(today());
      onAdded();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setPending(false);
    }
  }

  return (
    <form className="pay-form" onSubmit={onSubmit}>
      <input
        className="input pay-form__amount"
        type="number"
        step="0.01"
        min="0"
        inputMode="decimal"
        placeholder="Montant €"
        aria-label="Montant du paiement en euros"
        value={amount}
        onChange={(event) => setAmount(event.target.value)}
      />
      <input
        className="input pay-form__date"
        type="date"
        aria-label="Date du paiement"
        value={paidOn}
        onChange={(event) => event.target.value && setPaidOn(event.target.value)}
      />
      <button
        type="submit"
        className="button button--primary button--small"
        disabled={pending || !valid}
      >
        {pending ? 'Ajout…' : 'Ajouter un paiement'}
      </button>
      {error && (
        <p className="error pay-form__error" role="alert">
          {error}
        </p>
      )}
    </form>
  );
}
